import { StyledButton } from "@aureskonnect/react-ui";
import { CATALOGUE_MODE, ELECTRON_MODE, WEBVIEW_MODE } from "@constants";
import { Card } from "@mui/material";
import { t } from "i18next";
import { useSnapshot } from "valtio";

import React from "react";
import Modal from "react-modal";

import { setIsAppPaused, store } from "@store";

import { handleLedControl } from "@helpers/general";

import { PlaySvgIcon } from "@components/SvgIcons/PlaySvgIcon";
import { KeyboardWrapper } from "@components/SyncModal/KeyboardWrapper";
import { MiddlewareApiUrlInputWrapper } from "@components/SyncModal/MiddlewareApiUrlInputWrapper";

import {
    kioskStore,
    setIsLocalConfigModalOpened,
    setIsLocalSettingModalOpened,
    setIsMenuModalOpened,
} from "@pages/Kiosk/store";

enum systemActionsType {
    NoAction = "NoAction",
    Reset = "reset",
    ChangeMiddleware = "Change Middleware",
    Active = "pause",
    Update = "update",
    Restart = "restart",
    Close = "close",
    SwitchOff = "switch off",
}
type ConfirmationSystemActionsType = {
    setSystemActions: React.Dispatch<React.SetStateAction<systemActionsType>>;
    systemActions: systemActionsType;
    setIsSystemConfirmationOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isSystemConfirmationOpen: boolean;
};

export default function ConfirmationSystemActionsModelWrapper({
    setSystemActions,
    systemActions,
    setIsSystemConfirmationOpen,
    isSystemConfirmationOpen,
}: ConfirmationSystemActionsType): JSX.Element {
    const { isLedActive, connectedLedPort, isKeyboardOpened } =
        useSnapshot(kioskStore);
    const { isAppPaused } = useSnapshot(store);

    const [isInputValueInvalid, setIsInputValueInvalid] = React.useState(false);
    const [isFieldRequired, setIsFieldRequired] = React.useState(false);
    const [isUpdateMiddlware, setIsUpdateMiddlware] = React.useState(false);

    function useSystemAction(systemAction: string): void {
        const url = `http://localhost:6009/api/v1/kiosk?systemAction=${systemAction}`;
        fetch(url).catch((error) =>
            console.log(
                `Error while using system action with action: "${systemAction}" and Error: "${error.message}"`
            )
        );
    }

    function handleSystemActionOnclickEvent() {
        switch (systemActions) {
            case "reset":
                localStorage.clear();
                location.reload();
                break;
            case "Change Middleware":
                setIsUpdateMiddlware(true);
                break;
            case "pause":
                setIsAppPaused(!isAppPaused);
                setIsMenuModalOpened(false);
                setIsLocalConfigModalOpened(false);
                setIsLocalSettingModalOpened(false);
                setSystemActions(systemActionsType.NoAction);
                setIsSystemConfirmationOpen(false);
                if (isLedActive) {
                    handleLedControl(connectedLedPort, "Orange");
                }
                break;
            case "update":
                if (process.env.REACT_APP_MODE === ELECTRON_MODE) {
                    (window as RendererWindow).electronAPI.sendUpdateEvent();
                }
                setSystemActions(systemActionsType.NoAction);
                setIsSystemConfirmationOpen(false);
                break;
            case "restart":
                setSystemActions(systemActionsType.NoAction);
                setIsSystemConfirmationOpen(false);
                if (process.env.REACT_APP_MODE === WEBVIEW_MODE) {
                    useSystemAction("restart_poste");
                } else if (process.env.REACT_APP_MODE === ELECTRON_MODE) {
                    (window as RendererWindow).electronAPI.sendRestartEvent();
                }
                break;
            case "close":
                if (isLedActive) {
                    handleLedControl(connectedLedPort, "Disable");
                }
                setSystemActions(systemActionsType.NoAction);
                setIsSystemConfirmationOpen(false);
                if (process.env.REACT_APP_MODE === WEBVIEW_MODE) {
                    useSystemAction("close_app");
                } else if (process.env.REACT_APP_MODE === ELECTRON_MODE) {
                    (window as RendererWindow).electronAPI.sendQuitEvent();
                }
                break;
            case "switch off":
                setSystemActions(systemActionsType.NoAction);
                setIsSystemConfirmationOpen(false);

                if (process.env.REACT_APP_MODE === WEBVIEW_MODE) {
                    useSystemAction("shutDown_poste");
                } else if (process.env.REACT_APP_MODE === ELECTRON_MODE) {
                    (window as RendererWindow).electronAPI.sendShutdownEvent();
                }
                break;
            default:
                setSystemActions(systemActionsType.NoAction);
                setIsSystemConfirmationOpen(false);
        }
    }

    return (
        <Modal
            isOpen={isSystemConfirmationOpen}
            ariaHideApp={false}
            style={{
                overlay: {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "none",
                    zIndex: 3,
                },
                content: {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    padding: "0px",
                    color: "black",
                    height: systemActions !== "pause" ? "30rem" : "36rem",
                    width: "37rem",
                    zIndex: 2,
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #c2bebe",
                    bottom: "unset",
                    top: "unset",
                    right: "unset",
                    left: "unset",
                    zoom: `${
                        process.env.REACT_APP_MODE === CATALOGUE_MODE
                            ? "50%"
                            : "100%"
                    }`,
                },
            }}
        >
            {!isUpdateMiddlware ? (
                <div
                    className="d-flex flex-column justify-content-center align-items-center"
                    style={{
                        gap: "50px",
                    }}
                >
                    {systemActions === "pause" && (
                        <PlaySvgIcon
                            style={{
                                marginTop: "50px",
                            }}
                        />
                    )}

                    <div
                        style={{
                            textAlign: "left",
                            font: "normal normal bold 35px/55px Segoe UI",
                        }}
                    >
                        {t("Confirmation")}
                    </div>
                    <div
                        style={{
                            textAlign: "center",
                            font: "normal normal normal 25px/33px Segoe UI",
                            marginLeft: "100px",
                            marginRight: "100px",
                        }}
                    >
                        {t(
                            `Would you like to ${systemActions} your ${
                                systemActions === "restart" ||
                                systemActions === "switch off"
                                    ? "post"
                                    : "application"
                            } ?`
                        )}
                    </div>
                    <div className="d-flex">
                        <StyledButton
                            rounded={true}
                            className="m-2"
                            style={{
                                width: "179px",
                                height: "61px",
                                background:
                                    "#FFFFFF 0% 0% no-repeat padding-box",
                                boxShadow: "0px 3px 6px #00000029",
                                border: "1px solid #C9C9C9",
                                borderRadius: "12px",
                                color: "black",
                            }}
                            onClick={() => {
                                setIsSystemConfirmationOpen(false);
                            }}
                        >
                            {t("Back")}
                        </StyledButton>
                        <StyledButton
                            rounded={true}
                            className="m-2"
                            style={{
                                width: "233px",
                                height: "61px",
                                background:
                                    "#0a0a0a 0% 0% no-repeat padding-box",
                                boxShadow: "0px 3px 6px #00000029",
                                border: "1px solid #C9C9C9",
                                borderRadius: "12px",
                                color: "white",
                            }}
                            onClick={() => {
                                handleSystemActionOnclickEvent();
                            }}
                        >
                            {t("Confirm")}
                        </StyledButton>
                    </div>
                </div>
            ) : (
                <>
                    <MiddlewareApiUrlInputWrapper
                        isInputValueInvalid={isInputValueInvalid}
                        setIsInputValueInvalid={setIsInputValueInvalid}
                        isFieldRequired={isFieldRequired}
                        setIsSystemConfirmationOpen={
                            setIsSystemConfirmationOpen
                        }
                        setIsUpdateMiddlware={setIsUpdateMiddlware}
                        isUpdateMiddlware={isUpdateMiddlware}
                    />
                    {isKeyboardOpened === true ? (
                        <Card
                            style={{
                                position: "fixed",
                                bottom: 0,
                                width: "98vw",
                                left: "0.5rem",
                            }}
                            data-testid="keyboard-wrapper"
                        >
                            <KeyboardWrapper
                                setIsFieldRequired={setIsFieldRequired}
                                isInputValueInvalid={isInputValueInvalid}
                                setIsInputValueInvalid={setIsInputValueInvalid}
                            />
                        </Card>
                    ) : null}
                </>
            )}
        </Modal>
    );
}
